import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppShell } from './app/AppShell';
import { isAuthenticated } from './lib/token-store';
import { AuctionDetailPage } from './routes/AuctionDetailPage';
import { DashboardPage } from './routes/DashboardPage';
import { LoginPage } from './routes/LoginPage';
import { MembershipPage } from './routes/MembershipPage';
import { MyBidsPage } from './routes/MyBidsPage';
import { PaymentResultPage } from './routes/PaymentResultPage';
import { ForgotPasswordPage, ResetPasswordPage } from './routes/PasswordResetPages';
import { RegisterPage } from './routes/RegisterPage';
import { ShowcasePage } from './routes/ShowcasePage';
import { VehicleDetailPage } from './routes/VehicleDetailPage';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

// The whole app lives under the shell; the shell is NOT an auth guard (the showcase is public).
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: AppShell,
});

// Auth guard used by the login-gated routes only.
const requireSession = () => {
  if (!isAuthenticated()) throw redirect({ to: '/login' });
};

const showcaseRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/',
  component: ShowcasePage,
});
const loginRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/login',
  component: LoginPage,
  // Opt out of the shell's centred content column so the hero gradient can reach
  // both edges of the viewport (see AppShell).
  staticData: { fullBleed: true },
});
const registerRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/register',
  component: RegisterPage,
});
// Password reset (M19) — unauthenticated by necessity, and full-bleed like login so the hero
// reaches both edges.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
  staticData: { fullBleed: true },
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
  staticData: { fullBleed: true },
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});
// Login-gated (ADR 0027). M11 had opened both pages to anonymous visitors behind a server-side
// money mask; the 13.08.2026 product decision closed the auction surface entirely — an auction,
// its vehicles, their photos and their outcomes are members-only. The guard is the first of two
// layers: the API answers 401 without a session regardless of what the client does.
// `requiresSession` is the second layer, covering a different moment: `beforeLoad` runs on
// navigation, the shell flag holds while the page is mounted (see AppShell).
const auctionDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/auctions/$auctionId',
  beforeLoad: requireSession,
  staticData: { requiresSession: true },
  component: AuctionDetailPage,
});
const lotDetailRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/auctions/$auctionId/lots/$lotId',
  beforeLoad: requireSession,
  staticData: { requiresSession: true },
  component: VehicleDetailPage,
});
// The bidder's own panel, and where a successful login lands (Bia feedback, 11.08.2026).
// Registered before `/me/bids` for readability only — the paths are literal, not overlapping.
const dashboardRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/me',
  beforeLoad: requireSession,
  component: DashboardPage,
});
const myBidsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/me/bids',
  beforeLoad: requireSession,
  component: MyBidsPage,
});
const membershipRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/membership',
  beforeLoad: requireSession,
  component: MembershipPage,
});
// 3DS redirect landing pages — the payment callback lands here via PUBLIC_BIDDER_HOST.
const paymentResultRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/payments/$result',
  component: PaymentResultPage,
});

/** Exported for tests: lets a spec build a router over a memory history. */
export const routeTree = rootRoute.addChildren([
  shellRoute.addChildren([
    showcaseRoute,
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    auctionDetailRoute,
    lotDetailRoute,
    dashboardRoute,
    myBidsRoute,
    membershipRoute,
    paymentResultRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  /** Per-route flags the shell reads off the active match. */
  interface StaticDataRouteOption {
    /** Render this route edge-to-edge: no max-width column, no shell padding. */
    fullBleed?: boolean;
    /**
     * This route's content is members-only (ADR 0027). The shell withholds it as soon as
     * `authenticated` goes false, covering a session that ends *while* the page is mounted —
     * which `beforeLoad` never sees, because it only runs on navigation.
     */
    requiresSession?: boolean;
  }
}
